/*
  Deferred filter apply (KARA-68).

  collection-filters-form.js is minified and has no source in this repo, so it is not
  edited. It live-applies: any input inside [data-collection-sidebar] is handled on the
  bubble phase and submitted after a 500ms debounce, and the price slider does the same via
  a theme:range:update custom event on the same element.

  Both listeners are bubble-phase on the sidebar, so a capture-phase listener on the sidebar
  fires first and stopPropagation() starves them. Selections still land in the DOM; nothing
  is submitted. Apply then calls the theme's own submitForm(), which flushes every buffered
  selection at once and keeps its AJAX, pushState, results count and chip logic.

  submitForm() takes no arguments and survives minification. Verified 2026-07-17.

  Apply is the only commit path. Every other way out of the drawer drops what is buffered:
  a chip's ✕ navigates to url_to_remove and the server re-render overwrites the sidebar,
  and sort is handled below.
*/
(() => {
  const SIDEBAR = '[data-collection-sidebar]';
  const FORM = 'collection-filters-form';
  // Explicit hooks. data-collection-sidebar-close is shared by the ✕, the footer button and
  // the click-outside underlay, and data-filter-update-url is shared by every individual
  // filter-value clear link — neither identifies the control we mean.
  const APPLY = '[data-filters-apply]';
  const CLEAR = '[data-filters-clear]';
  // Sort sits outside the sidebar, so its clicks are never buffered, and the theme's sort
  // handler serializes the whole form.
  const SORT = '[data-sort-link]';
  const PRICE_MIN = '[data-field-price-min]';
  const PRICE_MAX = '[data-field-price-max]';
  const CHIPS = '[data-active-filters-chips]';
  const COMPONENT = 'collection-component';
  const HIDDEN = 'hidden';
  const APPLYING = 'is-applying';

  // Apply used to carry data-collection-sidebar-close, which shut the drawer synchronously
  // while submitForm()'s fetch was still in flight — the drawer vanished before results
  // loaded, reading as "applied" when it was not. Now Apply only submits; the close is held
  // until submitForm() re-renders the sidebar (a childList mutation the observer sees below).
  let applying = false;

  const swallow = (event) => event.stopPropagation();

  const init = () => {
    const sidebar = document.querySelector(SIDEBAR);
    const form = document.querySelector(FORM);
    if (!sidebar || !form || typeof form.submitForm !== 'function') return;

    // KARA-68: the chip row is server-rendered only inside the drawer, whose closed state
    // is off-canvas — so mirror it into [data-active-filters-chips] outside. The sidebar's
    // row is the source of truth: submitForm() re-renders it from the server on every
    // apply, and resetFilters() hides it, so observing the sidebar covers both.
    // ponytail: observer fires on every class flip inside the sidebar; the clone is a
    // handful of nodes, so no debounce until that measurably matters.
    syncChips();
    new MutationObserver((mutations) => {
      syncChips();

      // submitForm() sets [data-collection-sidebar].innerHTML when its fetch resolves — a
      // childList mutation. That is the "apply finished" signal: drop the loading state and
      // close the drawer now. Adding APPLYING is an attributes mutation, so it never trips
      // this branch prematurely.
      if (applying && mutations.some((m) => m.type === 'childList')) {
        applying = false;
        document.querySelector(SIDEBAR)?.classList.remove(APPLYING);
        document.querySelector(COMPONENT)?.dispatchEvent(new CustomEvent('theme:filter:close'));
      }
    }).observe(sidebar, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    // Buffer both channels the theme submits on. Swallowing also starves the theme's
    // input→slider path (server re-render), so typing a price moves the handles here.
    sidebar.addEventListener(
      'input',
      (event) => {
        swallow(event);
        if (event.target instanceof Element) syncSlider(event.target);
      },
      true
    );
    sidebar.addEventListener('theme:range:update', swallow, true);

    // submitForm() replaces the sidebar's innerHTML, so anything bound to a button inside
    // it dies on the first apply. Delegate from the document instead.
    //
    // For the same reason nothing inside the sidebar may be cached across an apply: the
    // <collection-filters-form> lives inside it, so that innerHTML swap destroys the element
    // and the browser upgrades a fresh instance from the new markup. A reference captured
    // here is detached from the second apply onward, and its internal this.form — the one
    // submitForm() builds FormData from — is a stale snapshot of the pre-apply DOM. Price
    // silently stopped applying that way. Re-query at click time.
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        // Apply: flush the buffer, hold the drawer open with a loading state, and let the
        // observer above close it once submitForm() re-renders the sidebar. preventDefault
        // stops the href="#filter-groups" hash jump the removed data-collection-sidebar-close
        // handler used to swallow.
        if (target.closest(APPLY)) {
          event.preventDefault();
          const liveSidebar = document.querySelector(SIDEBAR);
          const liveForm = document.querySelector(FORM);
          if (liveSidebar) clampPrice(liveSidebar);
          if (liveForm && typeof liveForm.submitForm === 'function') {
            applying = true;
            liveSidebar?.classList.add(APPLYING);
            liveForm.submitForm();
          }
          return;
        }

        // Sort: the theme rebuilds the URL from the whole form, which would commit
        // selections the user never applied. Reset the form to what the URL already says
        // first. Capture here runs before the theme's per-link bubble listener, and the
        // click must go through, so no stopPropagation.
        if (target.closest(SORT)) {
          const liveSidebar = document.querySelector(SIDEBAR);
          if (liveSidebar) syncToUrl(liveSidebar);
          return;
        }

        // Clear All: reset selections in place. Must not submit and must not navigate —
        // it is an <a href> to the unfiltered URL, which is the no-JS fallback.
        if (target.closest(CLEAR)) {
          event.preventDefault();
          event.stopPropagation();

          // The sidebar element itself survives the swap (only its innerHTML is replaced),
          // but re-query anyway so this does not depend on that staying true.
          const liveSidebar = document.querySelector(SIDEBAR);
          if (liveSidebar) resetFilters(liveSidebar);
        }
      },
      true
    );
  };

  // Clone the sidebar's chip row into the outside holder. Chips keep their url_to_remove
  // hrefs; outside the <collection-filters-form> the theme's AJAX handler never sees them,
  // so a click is a plain navigation — the chips' own no-JS fallback, which is correct.
  const syncChips = () => {
    const holder = document.querySelector(CHIPS);
    if (!holder) return;

    const row = document.querySelector(`${SIDEBAR} .collection__active__filters`);
    const show = row && !row.classList.contains(HIDDEN);

    holder.innerHTML = '';
    holder.hidden = !show;
    if (!show) return;

    const clone = row.cloneNode(true);
    // The sidebar row carries the drawer's entrance-animation hooks and whatever inline
    // state they left behind; outside the drawer they would replay or pin opacity.
    clone.className = 'collection__active__filters';
    clone.removeAttribute('style');
    ['data-animation', 'data-animation-delay', 'data-animation-duration'].forEach((attr) => clone.removeAttribute(attr));
    holder.appendChild(clone);
  };

  // Move the slider handle to match a typed price. Only the handle and the slider's
  // data-se-*-value attributes move — the input keeps whatever was typed, so the buffer
  // stays intact and clampPrice/Apply still decide what is submitted. Out-of-range values
  // pin the handle to the nearest bound; the other handle is the inner limit so they
  // never cross.
  const syncSlider = (input) => {
    const isMin = input.matches(PRICE_MIN);
    if (!isMin && !input.matches(PRICE_MAX)) return;

    const slider = input.closest('[data-range-holder]')?.querySelector('[data-range-slider]');
    if (!slider || typeof slider.setMinValue !== 'function') return;

    const lo = parseFloat(slider.getAttribute('data-se-min'));
    const hi = parseFloat(slider.getAttribute('data-se-max'));
    const typed = parseFloat(input.value);

    if (isMin) {
      const cap = parseFloat(slider.getAttribute('data-se-max-value'));
      slider.setMinValue(Math.min(Number.isNaN(typed) ? lo : Math.max(typed, lo), cap));
    } else {
      const floor = parseFloat(slider.getAttribute('data-se-min-value'));
      slider.setMaxValue(Math.max(Number.isNaN(typed) ? hi : Math.min(typed, hi), floor));
    }
  };

  // The slider lets the two handles cross by 10px, so a nudge can leave min above max and
  // return an empty grid. Collapse to a valid point range rather than swapping: the overlap
  // is small, so the bound being dragged is the one the user meant.
  const clampPrice = (sidebar) => {
    const min = sidebar.querySelector(PRICE_MIN);
    const max = sidebar.querySelector(PRICE_MAX);

    if (min && max && min.value && max.value && Number(min.value) > Number(max.value)) {
      min.value = max.value;
    }
  };

  // Drop everything unapplied by restating the form as the URL — i.e. as the server last
  // rendered it. The slider needs no handling here: every caller submits straight after,
  // and submitForm() re-renders the sidebar from the server.
  const syncToUrl = (sidebar) => {
    const params = new URLSearchParams(window.location.search);

    sidebar.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.checked = params.getAll(input.name).includes(input.value);
    });

    sidebar.querySelectorAll(`${PRICE_MIN}, ${PRICE_MAX}`).forEach((input) => {
      input.value = params.get(input.name) || '';
    });
  };

  const resetFilters = (sidebar) => {
    sidebar.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.checked = false;
    });

    // Price inputs carry their bounds as placeholders; clearing to empty means "unset",
    // which is what the theme reads when building the URL.
    sidebar.querySelectorAll(`${PRICE_MIN}, ${PRICE_MAX}`).forEach((input) => {
      input.value = '';
    });

    // The slider keeps its own state in data-se-*-value, and its onChange writes *both*
    // price inputs from those attributes — so leaving them stale means the next handle
    // nudge resurrects the bound just cleared. connectedCallback() re-lays out the dots;
    // calling it again is safe because it re-adds the same function references.
    sidebar.querySelectorAll('[data-range-slider]').forEach((slider) => {
      slider.setAttribute('data-se-min-value', slider.getAttribute('data-se-min'));
      slider.setAttribute('data-se-max-value', slider.getAttribute('data-se-max'));
      if (typeof slider.connectedCallback === 'function') slider.connectedCallback();
    });

    // Nothing is submitted yet, so the server-rendered chips and count would otherwise go on
    // claiming filters are active and Clear All would read as a no-op. The count outside the
    // sidebar has to be reached from the document.
    const chips = sidebar.querySelector('.collection__active__filters');
    if (chips) chips.classList.add(HIDDEN);

    document.querySelectorAll('[data-active-filters]').forEach((count) => {
      count.textContent = '0';
      count.classList.add(HIDDEN);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
